import { useNavigate } from "react-router-dom";
import { Avatar } from "../../ui";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { usePatientsBase } from "../../../utils/hubPaths";
import { clientStatusBadges } from "./clientSportDisplay";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import s from "../clinic.module.css";

function membershipLabel(membership) {
  if (!membership) return null;
  if (membership.status === "pending") return "не оплачен";
  if (membership.status === "expired") return "истёк";
  if (Number(membership.amount_due) > 0) {
    return `долг ${Math.round(Number(membership.amount_due))} ₽`;
  }
  if (membership.plan_type === "visits" && membership.visits_remaining != null) {
    const n = membership.visits_remaining;
    return `${n} ${n === 1 ? "занятие" : n < 5 ? "занятия" : "занятий"} осталось`;
  }
  if (membership.plan_name) return membership.plan_name;
  return null;
}

function groupLabel(sport) {
  if (sport.children?.length) return null;
  if (sport.account_kind === "guardian") return "Родитель";
  if (!sport.groups?.length) return "Без группы";
  const primary = sport.groups[0];
  return sport.groups.length > 1
    ? `${primary} +${sport.groups.length - 1}`
    : primary;
}

function childrenLabel(sport) {
  if (!sport.children?.length) return null;
  const n = sport.children.length;
  const count =
    n === 1 ? "1 ребёнок" : n < 5 ? `${n} ребёнка` : `${n} детей`;
  const names = sport.children
    .slice(0, 2)
    .map((c) => c.full_name)
    .filter(Boolean)
    .join(", ");
  return names ? `${count}: ${names}` : count;
}

function clientSubtitle(sport) {
  const parts = [];
  const kids = childrenLabel(sport);
  if (kids) {
    parts.push(kids);
    if (sport.also_guardian && sport.groups?.length) {
      parts.push(groupLabel(sport));
    }
  } else {
    const group = groupLabel(sport);
    if (group) parts.push(group);
  }

  const membership = membershipLabel(sport.membership);
  if (membership) {
    parts.push(membership);
  } else if (
    sport.account_kind !== "guardian" &&
    !sport.warnings?.includes("unpaid_membership")
  ) {
    parts.push("без абонемента");
  }

  if (!parts.length) {
    return sport.account_kind === "guardian" ? "Родитель" : "Без группы";
  }
  return parts.join(" · ");
}

function parentLine(sport) {
  const g = sport?.primary_guardian;
  if (!g?.full_name || sport?.account_kind === "guardian") return null;
  const phone = g.phone ? ` · ${g.phone}` : "";
  return `Родитель: ${g.full_name}${phone}`;
}

function guardianBadge(sport) {
  if (!sport?.also_guardian || sport?.account_kind === "guardian") return null;
  const n = sport.children?.length;
  if (!n) return null;
  return n === 1 ? "Родитель" : `Родитель · ${n}`;
}

function desktopMetaChips(sport) {
  const chips = [];
  const kids = childrenLabel(sport);
  if (kids) {
    chips.push({ key: "kids", label: kids, tone: "muted" });
  } else {
    const group = groupLabel(sport);
    if (group) chips.push({ key: "group", label: group, tone: "muted" });
  }

  const membership = membershipLabel(sport.membership);
  if (membership) {
    const unpaid =
      sport.membership?.status === "pending" ||
      Number(sport.membership?.amount_due) > 0;
    chips.push({
      key: "membership",
      label: membership,
      tone: unpaid ? "warn" : "ok",
    });
  } else if (
    sport.account_kind !== "guardian" &&
    !sport.warnings?.includes("unpaid_membership")
  ) {
    chips.push({ key: "membership", label: "без абонемента", tone: "warn" });
  }

  return chips;
}

export default function ClientListCard({ client, variant = "default" }) {
  const navigate = useNavigate();
  const patientsBase = usePatientsBase();
  const sport = client?.sport || {};
  const badges = clientStatusBadges(sport);
  const badge = badges[0];
  const guardianTag = guardianBadge(sport);
  const isApp = variant === "app";
  const isDesktop = variant === "desktop";
  const parentMeta = parentLine(sport);
  const chips = isDesktop ? desktopMetaChips(sport) : [];

  const openProfile = () => {
    navigate(`${patientsBase}/${client.id}`);
  };

  if (isDesktop) {
    return (
      <article
        className={`${md.desktopClientCard} ${
          sport.is_archived ? md.desktopClientCardArchived : ""
        }`}
      >
        <button
          type="button"
          className={md.desktopClientCardBtn}
          onClick={openProfile}
        >
          <Avatar
            name={client.full_name}
            src={mediaUrl(client.photo_url)}
            size="md"
            className={md.desktopClientAvatar}
          />
          <span className={md.desktopClientBody}>
            <span className={md.desktopClientTop}>
              <strong className={md.desktopClientName}>
                {client.full_name || "Без имени"}
              </strong>
              {badge ? (
                <span
                  className={`${md.desktopClientBadge} ${
                    md[`desktopClientBadge_${badge.tone}`] || ""
                  }`}
                >
                  {badge.label}
                </span>
              ) : null}
              {guardianTag ? (
                <span
                  className={`${md.desktopClientBadge} ${md.desktopClientBadge_muted}`}
                >
                  {guardianTag}
                </span>
              ) : null}
            </span>
            {chips.length || client.phone ? (
              <span className={md.desktopClientMetaLine}>
                {chips.length ? (
                  <span className={md.desktopClientChips}>
                    {chips.map((chip) => (
                      <span
                        key={chip.key}
                        className={`${md.desktopClientChip} ${
                          md[`desktopClientChip_${chip.tone}`] || ""
                        }`}
                      >
                        {chip.label}
                      </span>
                    ))}
                  </span>
                ) : null}
                {client.phone ? (
                  <span className={md.desktopClientPhone}>{client.phone}</span>
                ) : null}
              </span>
            ) : null}
            {parentMeta ? (
              <span className={md.desktopClientParent}>{parentMeta}</span>
            ) : null}
          </span>
          <span className={md.desktopClientChevron} aria-hidden />
        </button>
      </article>
    );
  }

  return (
    <article
      className={`${isApp ? aa.clientRow : s.clientCardSimple} ${
        sport.is_archived
          ? isApp
            ? aa.clientRowArchived
            : s.clientCardSimple_archived
          : ""
      }`}
    >
      <button
        type="button"
        className={isApp ? aa.clientRowBtn : s.clientCardSimpleBtn}
        onClick={openProfile}
      >
        <Avatar name={client.full_name} src={mediaUrl(client.photo_url)} size="md" />
        <span className={isApp ? aa.clientRowBody : s.clientCardSimpleBody}>
          <span className={isApp ? aa.clientRowTop : s.clientCardSimpleTop}>
            <strong className={isApp ? aa.clientRowName : s.clientCardSimpleName}>
              {client.full_name || "Без имени"}
            </strong>
            {badge ? (
              <span
                className={`${isApp ? aa.clientRowBadge : s.clientCardSimpleBadge} ${
                  isApp
                    ? aa[`clientRowBadge_${badge.tone}`]
                    : s[`clientCardSimpleBadge_${badge.tone}`]
                }`}
              >
                {badge.label}
              </span>
            ) : null}
            {guardianTag ? (
              <span
                className={`${isApp ? aa.clientRowBadge : s.clientCardSimpleBadge} ${
                  isApp ? aa.clientRowBadge_muted : s.clientCardSimpleBadge_muted
                }`}
              >
                {guardianTag}
              </span>
            ) : null}
          </span>
          <span className={isApp ? aa.clientRowMeta : s.clientCardSimpleMeta}>
            {clientSubtitle(sport)}
          </span>
          {parentMeta ? (
            <span className={isApp ? aa.clientRowParent : s.clientCardSimpleParent}>
              {parentMeta}
            </span>
          ) : null}
          {client.phone ? (
            <span className={isApp ? aa.clientRowPhone : s.clientCardSimplePhone}>
              {client.phone}
            </span>
          ) : null}
        </span>
        <span
          className={isApp ? aa.clientRowChevron : s.clientCardSimpleChevron}
          aria-hidden
        />
      </button>
    </article>
  );
}
